/* RIE-1 · Reconciling Items panel — Workspace step 4 affordance.
 * Renders the existing reconciling items for the period, lets the
 * controller disclose a bank variance as a first-class Reconciling Item
 * (via POST), and inline-edit / delete via PATCH / DELETE.
 *
 * Locked periods render read-only (server enforces 403 regardless). */
import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";

const CATEGORY_LABELS = {
  outstanding_deposit:  "Outstanding deposit",
  outstanding_check:    "Outstanding check",
  timing_difference:    "Timing difference",
  bank_error:           "Bank error",
  duplicate_residual:   "Duplicate residual",
  post_close_adjustment:"Post-close adjustment",
  other:                "Other",
};

export default function ReconcilingItemsPanel({ periodId, period, bankVariance, onChanged }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // ri being edited
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const isLocked = period?.status === "locked";

  useEffect(() => {
    if (!periodId) return;
    setLoading(true);
    Promise.all([
      api.listReconcilingItems(periodId),
      api.listRiCategories().catch(() => ({ categories: Object.keys(CATEGORY_LABELS) })),
    ]).then(([rows, cats]) => {
      setItems(rows);
      setCategories(cats.categories);
      setLoading(false);
    }).catch(e => {
      setErr(e?.response?.data?.detail || e?.message);
      setLoading(false);
    });
  }, [periodId]);

  async function refreshList() {
    const rows = await api.listReconcilingItems(periodId);
    setItems(rows);
    onChanged && onChanged();
  }

  async function handleCreate(body) {
    setBusy(true); setErr(null);
    try {
      await api.createReconcilingItem(periodId, body);
      setShowForm(false);
      await refreshList();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleUpdate(riId, body) {
    setBusy(true); setErr(null);
    try {
      await api.updateReconcilingItem(riId, body);
      setEditing(null);
      await refreshList();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(riId) {
    if (!window.confirm("Delete this reconciling item? "
                        + "The variance will no longer be disclosed.")) return;
    setBusy(true); setErr(null);
    try {
      await api.deleteReconcilingItem(riId);
      await refreshList();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  }

  const total = items.reduce((s, r) => s + Number(r.amount || 0), 0);
  const hasMaterialVariance = Math.abs(bankVariance || 0) > 0.01;

  return (
    <section className="ri-panel" data-testid="ri-panel"
             style={{ marginTop: 16, padding: "14px 16px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 8, background: "rgba(255,255,255,0.02)" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="heading h3" style={{ marginBottom: 2 }}>
            Reconciling Items
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Disclose residual bank variance as a first-class reconciling
            item. Each disclosed item demotes the verdict to
            <em> CERTIFIED w/ Exceptions</em> rather than blocking the close.
          </div>
        </div>
        {!isLocked && (
          <button
            type="button"
            className="btn"
            data-testid="ri-add-btn"
            onClick={() => {
              setShowForm(true);
              setEditing(null);
            }}
            disabled={busy}
            style={{ padding: "7px 14px", borderRadius: 6,
                     background: "#1e5d34", color: "#fff", border: "none",
                     cursor: "pointer", fontWeight: 600 }}>
            + Disclose bank variance
          </button>
        )}
      </div>

      {err && (
        <div data-testid="ri-error"
             style={{ marginTop: 10, padding: "6px 10px", borderRadius: 4,
                      background: "rgba(138,42,12,0.18)", color: "#fff",
                      fontSize: 13 }}>
          {String(err).slice(0, 280)}
        </div>
      )}

      {loading && <div className="muted" style={{ marginTop: 10 }}>Loading…</div>}

      {!loading && items.length === 0 && !showForm && (
        <div className="muted" data-testid="ri-empty"
             style={{ marginTop: 12, fontSize: 13 }}>
          No reconciling items.
          {hasMaterialVariance && !isLocked && " The current bank variance "
            + "of " + fmt.currency(bankVariance) + " is undisclosed — consider "
            + "adding a reconciling item to document it."}
        </div>
      )}

      {!loading && items.length > 0 && (
        <table data-testid="ri-table"
               style={{ width: "100%", marginTop: 12, borderCollapse: "collapse",
                        fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left",
                         borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <th style={{ padding: "6px 8px", width: 180 }}>Category</th>
              <th style={{ padding: "6px 8px", width: 120,
                           textAlign: "right" }}>Amount</th>
              <th style={{ padding: "6px 8px" }}>Description</th>
              {!isLocked && <th style={{ padding: "6px 8px", width: 120 }}></th>}
            </tr>
          </thead>
          <tbody>
            {items.map(ri => (
              editing && editing.id === ri.id ? (
                <RiEditRow key={ri.id} ri={editing} categories={categories}
                           onCancel={() => setEditing(null)}
                           onSave={body => handleUpdate(ri.id, body)}
                           busy={busy} />
              ) : (
                <RiRow key={ri.id} ri={ri} isLocked={isLocked}
                       onEdit={() => setEditing(ri)}
                       onDelete={() => handleDelete(ri.id)} />
              )
            ))}
            <tr style={{ borderTop: "1px solid rgba(255,255,255,0.10)",
                         fontWeight: 600 }}
                data-testid="ri-total-row">
              <td style={{ padding: "6px 8px" }} className="muted">
                Total disclosed
              </td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>
                ${fmt.currency(total)}
              </td>
              <td></td>
              {!isLocked && <td></td>}
            </tr>
          </tbody>
        </table>
      )}

      {showForm && !editing && !isLocked && (
        <RiForm
          categories={categories}
          defaultAmount={hasMaterialVariance ? -bankVariance : 0}
          onCancel={() => setShowForm(false)}
          onSave={handleCreate}
          busy={busy}
        />
      )}
    </section>
  );
}


function RiRow({ ri, isLocked, onEdit, onDelete }) {
  return (
    <tr data-testid={`ri-row-${ri.id}`}
        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <td style={{ padding: "6px 8px" }}>
        {CATEGORY_LABELS[ri.category] || ri.category}
      </td>
      <td style={{ padding: "6px 8px", textAlign: "right" }}
          className={Number(ri.amount) >= 0 ? "" : "muted"}>
        ${Number(ri.amount).toLocaleString(undefined, {
          minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td style={{ padding: "6px 8px" }}>{ri.description}</td>
      {!isLocked && (
        <td style={{ padding: "6px 8px", textAlign: "right" }}>
          <button data-testid={`ri-edit-${ri.id}`}
                  className="btn-link" onClick={onEdit}
                  style={btnLinkStyle("#9bb4ff")}>edit</button>
          <button data-testid={`ri-delete-${ri.id}`}
                  className="btn-link" onClick={onDelete}
                  style={{ ...btnLinkStyle("#ff9b9b"), marginLeft: 12 }}>
            delete
          </button>
        </td>
      )}
    </tr>
  );
}


function RiEditRow({ ri, categories, onCancel, onSave, busy }) {
  const [form, setForm] = useState({
    category: ri.category,
    amount: String(ri.amount ?? ""),
    description: ri.description ?? "",
  });
  return (
    <tr data-testid={`ri-edit-row-${ri.id}`}
        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)",
                 background: "rgba(255,255,255,0.03)" }}>
      <td style={{ padding: "6px 8px" }}>
        <select
          data-testid="ri-edit-category"
          value={form.category}
          onChange={e => setForm({ ...form, category: e.target.value })}
          style={inputStyle}>
          {categories.map(c => (
            <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
          ))}
        </select>
      </td>
      <td style={{ padding: "6px 8px", textAlign: "right" }}>
        <input
          data-testid="ri-edit-amount"
          type="number" step="0.01" value={form.amount}
          onChange={e => setForm({ ...form, amount: e.target.value })}
          style={{ ...inputStyle, width: 100, textAlign: "right" }} />
      </td>
      <td style={{ padding: "6px 8px" }}>
        <input
          data-testid="ri-edit-description"
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          style={inputStyle} />
      </td>
      <td style={{ padding: "6px 8px", textAlign: "right" }}>
        <button
          data-testid="ri-edit-save"
          disabled={busy || !form.description.trim()}
          onClick={() => onSave({
            category: form.category,
            amount: Number(form.amount),
            description: form.description.trim(),
          })}
          style={btnLinkStyle("#9bff9b")}>save</button>
        <button
          data-testid="ri-edit-cancel"
          onClick={onCancel}
          style={{ ...btnLinkStyle("#888"), marginLeft: 12 }}>cancel</button>
      </td>
    </tr>
  );
}


function RiForm({ categories, defaultAmount, onCancel, onSave, busy }) {
  const [form, setForm] = useState({
    category: "outstanding_check",
    amount: defaultAmount ? defaultAmount.toFixed(2) : "",
    description: "",
  });
  const canSave = form.description.trim().length > 0
                  && form.amount !== ""
                  && !Number.isNaN(Number(form.amount));
  return (
    <div data-testid="ri-form" style={{
      marginTop: 14, padding: "12px 14px", borderRadius: 6,
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
    }}>
      <div className="heading h3" style={{ fontSize: 14, marginBottom: 10 }}>
        Disclose bank variance as Reconciling Item
      </div>
      <div style={{ display: "grid", gap: 10,
                    gridTemplateColumns: "200px 140px 1fr",
                    alignItems: "center" }}>
        <label className="muted" style={{ fontSize: 12 }}>
          Category
          <select
            data-testid="ri-form-category"
            value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })}
            style={{ ...inputStyle, width: "100%", marginTop: 4 }}>
            {categories.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
        </label>
        <label className="muted" style={{ fontSize: 12 }}>
          Amount
          <input
            data-testid="ri-form-amount"
            type="number" step="0.01" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            style={{ ...inputStyle, width: "100%", marginTop: 4,
                     textAlign: "right" }} />
        </label>
        <label className="muted" style={{ fontSize: 12 }}>
          Description
          <input
            data-testid="ri-form-description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder="e.g. Cheque #4421 not yet cleared as of period end"
            style={{ ...inputStyle, width: "100%", marginTop: 4 }} />
        </label>
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 10,
                    justifyContent: "flex-end" }}>
        <button data-testid="ri-form-cancel"
                onClick={onCancel} disabled={busy}
                style={btnLinkStyle("#888")}>cancel</button>
        <button
          data-testid="ri-form-save"
          onClick={() => onSave({
            category: form.category,
            amount: Number(form.amount),
            description: form.description.trim(),
          })}
          disabled={!canSave || busy}
          style={{ padding: "6px 14px", borderRadius: 6,
                   background: canSave && !busy ? "#1e5d34" : "#444",
                   color: "#fff", border: "none",
                   cursor: canSave && !busy ? "pointer" : "not-allowed",
                   fontWeight: 600 }}>
          {busy ? "Saving…" : "Disclose"}
        </button>
      </div>
    </div>
  );
}


const inputStyle = {
  padding: "5px 8px", borderRadius: 4,
  border: "1px solid rgba(255,255,255,0.16)",
  background: "rgba(0,0,0,0.25)", color: "#eee",
  fontSize: 13,
};

const btnLinkStyle = color => ({
  background: "none", border: "none", color, cursor: "pointer",
  padding: 0, fontSize: 12, textDecoration: "underline",
});
